import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge } from 'rxjs';
import { environment } from '@environments/environment';

import { debounceTime } from 'rxjs/operators';

const INACTIVITY_TIMEOUT_MS = environment.TIMEOUT_INACTIVITY;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
const ACTIVITY_DEBOUNCE_MS = 1000;

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private readonly _timeout = new Subject<void>();

  public readonly timeout$ = this._timeout.asObservable();

  private activitySubscription?: Subscription;
  private timer?: ReturnType<typeof setTimeout>;
  private watching = false;

  constructor(private readonly ngZone: NgZone) {}

  startWatching(): void {
    if (this.watching) return;
    this.watching = true;

    this.ngZone.runOutsideAngular(() => {
      this.activitySubscription = merge(
        ...ACTIVITY_EVENTS.map((eventName) => fromEvent(document, eventName)),
      )
        .pipe(debounceTime(ACTIVITY_DEBOUNCE_MS))
        .subscribe(() => this.resetTimer());

      this.resetTimer();
    });
  }

  stopWatching(): void {
    this.watching = false;
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.clearTimer();
  }

  private resetTimer(): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.ngZone.run(() => this._timeout.next());
    }, INACTIVITY_TIMEOUT_MS);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }
}
